/**
 * SwimMode — Phase 0's deliverable: drive a shark over the seafloor with real momentum.
 * Phase 1 upgrades the "grey box" in place: the thing you steer is a live voxel shark.
 */
import * as THREE from 'three';
import type { Mode, ModeContext } from './Mode.ts';
import { Player } from '../entities/Player.ts';
import { Seafloor } from '../render/Seafloor.ts';
import { Algae, MAX_DISTURBERS } from '../render/Algae.ts';
import { Wake } from '../world/Wake.ts';
import { DayNight } from '../world/DayNight.ts';
import { LightShafts } from '../render/LightShafts.ts';
import { Particulate } from '../render/Particulate.ts';
import { WaterSurface } from '../render/WaterSurface.ts';
import { WaterVolume } from '../render/WaterVolume.ts';
import { ShadowPass } from '../render/ShadowPass.ts';
import { Props } from '../world/Props.ts';
import { FishSchool } from '../entities/FishSchool.ts';
import { Autopilot } from '../entities/Autopilot.ts';
import { Octopus } from '../entities/Octopus.ts';
import { Turtle } from '../entities/Turtle.ts';
import { Manta } from '../entities/Manta.ts';
import { Feeder, feederAt, FEEDER_PERIOD } from '../entities/Feeder.ts';
import { Angler } from '../entities/Angler.ts';
import { applyLure, makeLureState } from '../render/LureLight.ts';
import { RemoteSharks } from '../entities/RemoteSharks.ts';
import { NetClient } from '../net/NetClient.ts';
import { SharkMaterial } from '../render/SharkMaterial.ts';
import { defaultSpec, speciesIndex, type SharkSpec } from '../voxel/SharkForge.ts';

/* Ids for the reel's local companions. Well clear of the relay's, which start at 1 and are
   16-bit, so a reel running against a live bay cannot collide with a real player. */
const REEL_PEER_ID = 60000;
import { LUNGE, MOVEMENT, OCEAN, WATER } from '../data/tuning.ts';
import { bus } from '../core/Events.ts';
import { waterDepth, COAST } from '../world/Coast.ts';



/** how far the sun is occluded in full shade; ambient still fills, so never 1 */
const SHADOW_STRENGTH = 0.82;

export class SwimMode implements Mode {
  readonly name = 'swim' as const;

  private readonly ctx: ModeContext;
  private readonly group = new THREE.Group();
  private readonly seafloor = new Seafloor();
  private readonly algae = new Algae();
  private readonly wake = new Wake();
  private readonly sky = new DayNight();
  private readonly reef = new Props();
  private readonly fish = new FishSchool();
  private readonly octopus = new Octopus();
  private readonly turtles = new Turtle();
  private readonly mantas = new Manta();
  private readonly feeder = new Feeder();
  /* Anglerfish on the drop-off. Two, not more: the point of them is that the dark is
     mostly empty and finding one is an event. */
  private readonly anglers = new Angler(2);
  private readonly lure = makeLureState();
  private readonly net = new NetClient();
  private readonly remotes: RemoteSharks;
  private readonly autopilot = new Autopilot();
  private readonly shafts = new LightShafts();
  private readonly snow = new Particulate();
  private readonly surface = new WaterSurface();
  private readonly volume = new WaterVolume();
  private readonly shadows = new ShadowPass();
  /** rebuilt whenever the reef or the player mesh changes, not per frame */
  private casters: Array<THREE.Mesh | THREE.InstancedMesh> = [];
  private castersDirty = true;
  private octopusOut = 0;
  private readonly player = new Player();
  // JAW compiles the articulation stages. Harmless on species without a rig: the jawGroup
  // attribute is simply absent, WebGL reads 0, and neither head branch is taken.
  private readonly material = new SharkMaterial({ jaw: true });

  private spec: SharkSpec = defaultSpec();
  private companions = 0;
  /* Solo runs the SAME protocol against a local stand-in rather than a second code path: a
     timer here plays the relay's part, and everything downstream cannot tell the difference. */
  private soloNext = 14;
  private soloId = 0;
  private lastWinner = '';
  private winnerAt = -99;
  /** reel seconds while a reel is playing, negative otherwise */
  private reelClock = -1;
  private bay = '';
  private bayListed = false;
  private seed = 1;
  /** monotonically increasing; a build whose token is stale is discarded */
  private buildToken = 0;
  private triangles = 0;
  private status = 'BUILDING';
  private unsubLunge: (() => void) | null = null;

  // scratch — the render path must not allocate
  private readonly followTarget = { x: 0, y: 0, z: 0 };
  private readonly followVel = { x: 0, z: 0 };

  constructor(ctx: ModeContext) {
    this.ctx = ctx;
    this.remotes = new RemoteSharks(ctx.mesh);

    this.material.applyWaterConfig(WATER);
    this.group.add(this.volume.mesh);
    this.group.add(this.seafloor.mesh);
    this.group.add(this.algae.mesh);
    this.group.add(this.shafts.mesh);
    this.group.add(this.snow.points);
    this.group.add(this.surface.mesh);
    this.group.add(this.reef.group);
    this.group.add(this.fish.group);
    this.group.add(this.octopus.group);
    this.group.add(this.turtles.group);
    this.group.add(this.mantas.group);
    this.group.add(this.feeder.group);
    this.group.add(this.anglers.group);
    this.group.add(this.remotes.group);
    this.group.add(this.player.visual.object);
  }

  enter(seed: number): void {
    this.seed = seed;
    this.ctx.scene.add(this.group);
    // FOV punch on lunge (SPEC §13) — the camera reacts, the player doesn't know about it
    this.unsubLunge = bus.on('player:lunge', () => {
      this.ctx.camera.punchFov(LUNGE.fovPunch, LUNGE.fovPunchTime);
    });
    void this.rebuild();
    this.joinReef();
  }

  /**
   * Which bay to swim in. Set by the landing page before `enter`; left empty for solo, which
   * is the case that must never open a socket at all.
   *
   * @param listed whether the bay appears in the public list — only its creator sets this
   */
  /**
   * The animal the player built on the landing page. Set before `enter`.
   *
   * Also what is published to the bay: peers rebuild it from these five numbers on their own
   * worker, so no geometry crosses the network and a shark somebody tuned arrives intact.
   */
  setSpec(spec: SharkSpec): void {
    this.spec = { ...spec };
    void this.rebuild();
  }

  get sharkName(): string {
    return this.spec.name;
  }

  setBay(code: string, listed: boolean): void {
    this.bay = code;
    this.bayListed = listed;
    // Everyone in a bay must be under the same sky. Solo keeps its own clock, which starts
    // mid-morning so a first-time player does not arrive in the dark.
    if (code !== '') this.sky.syncToWallClock();
  }

  /** the bay in progress, '' when solo. What the invite panel shares. */
  get bayCode(): string {
    return this.bay;
  }

  /** true once the relay has actually welcomed us into that bay */
  get bayJoined(): boolean {
    return this.net.connected;
  }

  /** other sharks currently in the bay */
  get peerCount(): number {
    return this.remotes.count;
  }

  /* ---- the teaser reel ------------------------------------------------------------------
     Four hooks and no more. The reel drives the sky and some company and then gets out of the
     way: everything it films — the kelp parting, the reef streaming, the shafts fading with the
     sun — is the game doing what it always does. A capture path that special-cased the world
     would produce footage of something nobody can play. */

  /** pin the sky, for a shot that must look the same on every retake */
  setSkyPhase(phase: number): void {
    this.sky.setPhase(phase);
  }

  /** seconds of sim per second of real time; 0 freezes the sky where it is */
  setSkyRate(rate: number): void {
    this.sky.rate = rate;
  }

  /** true once the shark has been built and the reef around it has streamed in */
  worldReady(): boolean {
    return this.player.visual.object.visible && this.triangles > 0;
  }

  /**
   * Companions for the reel, driven locally through the same path as real peers.
   *
   * Fed as poses rather than posed directly: RemoteSharks interpolates, dead-reckons and derives
   * the bank and body bend from heading rate, and a companion that skipped all that would swim
   * differently from a genuine peer in the same shot. The point of filming it is that it is the
   * real thing.
   */
  setCompanions(n: number): void {
    const want = Math.max(0, Math.min(4, n));
    if (want === this.companions) return;
    while (this.companions > want) {
      this.remotes.remove(REEL_PEER_ID + this.companions);
      this.companions--;
    }
    while (this.companions < want) {
      this.companions++;
      const i = this.companions;
      this.remotes.add({
        id: REEL_PEER_ID + i,
        // A different species each, so the shot reads as several animals rather than a shoal.
        species: (speciesIndex(this.spec.species) + i * 3) % 8,
        seed: (this.spec.seed ^ (i * 0x9e3779b9)) >>> 0,
        size: 0.85 + i * 0.14,
        age: (i * 0.31) % 1,
        name: `FRIEND ${i}`,
      });
    }
  }

  /**
   * Put the shark somewhere, for a reel cut.
   *
   * A hard move, and that is exactly right for a trailer: the beats are CUTS, so arriving in
   * the kelp or out over the drop-off is a new shot rather than a two-minute swim nobody
   * filmed. The camera reacquires rather than easing across the map, which is what the follow
   * rig already does coming back from the lab.
   */
  placePlayer(x: number, z: number, heading: number): void {
    this.player.reset(x, z, heading);
  }

  /** move the reel's companions; called from the render loop while a reel is running */
  driveCompanions(now: number): void {
    this.reelClock = now;
    if (this.companions === 0) return;
    const p = this.player;
    const poses = [];
    for (let i = 1; i <= this.companions; i++) {
      /* Formation, not a follow: each holds a station beside and behind the player and breathes
         in and out of it. A chase would put them all in a line and read as a queue. */
      const phase = now * (0.21 + i * 0.05) + i * 2.4;
      const side = (i % 2 === 0 ? 1 : -1) * (5.5 + i * 1.6 + Math.sin(phase) * 1.7);
      const back = 3.5 + i * 2.7 + Math.cos(phase * 0.8) * 1.6;
      const sh = Math.sin(p.heading);
      const ch = Math.cos(p.heading);
      poses.push({
        id: REEL_PEER_ID + i,
        x: p.renderX - sh * back + ch * side,
        y: p.renderY + Math.sin(phase * 0.6) * 0.9 + (i - 1.5) * 0.5,
        z: p.renderZ - ch * back - sh * side,
        heading: p.heading + Math.sin(phase * 0.5) * 0.16,
        speed: p.speed,
      });
    }
    // Stamped as if it had just arrived from a relay with no latency, so the interpolation and
    // dead reckoning behave exactly as they do for a real peer.
    this.remotes.receive(now, now * 1000, poses);
  }

  /**
   * Connect, if a relay is configured AND we were sent to a bay. Everything below tolerates
   * the absence of either: with no VITE_RELAY_URL, or with no bay, the client never opens a
   * socket and this is exactly the single-player game.
   */
  private joinReef(): void {
    if (!this.net.enabled || this.bay === '') return;
    this.net.on('welcome', (_id, _world, peers) => {
      this.remotes.clear();
      for (const p of peers) this.remotes.add(p);
    });
    this.net.on('joined', (p) => this.remotes.add(p));
    this.net.on('left', (id) => this.remotes.remove(id));
    this.net.on('snapshot', (sentAt, poses) =>
      this.remotes.receive(performance.now() / 1000, sentAt, poses));
    this.net.on('status', (up) => { if (!up) this.remotes.clear(); });
    this.net.on('feeder', (id) => this.feeder.show(feederAt(this.seed, id)));
    this.net.on('fed', (_id, by, name) => {
      // `by` is a relay id; ours means we got it. Everyone renders the prize on the winner.
      this.feeder.taken(by === this.net.id ? 1 : by);
      this.lastWinner = by === this.net.id ? 'YOU' : (name || 'SOMEONE');
      this.winnerAt = performance.now() / 1000;
    });
    this.net.connect({
      species: speciesIndex(this.spec.species),
      seed: this.spec.seed,
      size: this.spec.size,
      age: this.spec.age,
      name: this.spec.name,
    }, this.bay, this.bayListed);
  }

  exit(): void {
    this.ctx.scene.remove(this.group);
    this.unsubLunge?.();
    this.unsubLunge = null;
  }

  setSeed(seed: number): void {
    if (seed === this.seed) return;
    this.seed = seed;
    void this.rebuild();
  }


  private async rebuild(): Promise<void> {
    const token = ++this.buildToken;
    this.status = 'BUILDING';
    try {
      const built = await this.ctx.mesh.buildSpec(this.spec);
      if (token !== this.buildToken) {
        built.geometry.dispose();
        return;
      }
      this.player.setMesh(built, this.material);
      this.castersDirty = true;
      this.triangles = built.triangleCount;
      this.status = `${built.triangleCount} TRI / ${built.buildMs.toFixed(1)} MS`;
    } catch (err) {
      this.status = `FAILED: ${(err as Error).message}`;
      console.error('[SwimMode] shark build failed', err);
    }
  }

  fixedUpdate(dt: number): void {
    const p = this.player;
    /* The on-screen pad points at a screen direction; turning that into a rudder needs to know
       which way the shark is already facing. Fed back here rather than read by Input, so the
       input layer still knows nothing about the game. */
    this.ctx.input.setViewHeading(p.heading);
    // Autopilot runs on the fixed step alongside the physics it feeds, so its steering is
    // deterministic with the simulation rather than sampled at whatever the frame rate is.
    const pilot = this.autopilot.update(dt, this.ctx.input, p.x, p.z, p.y, p.heading, p.speed);
    p.fixedUpdate(dt, this.ctx.input, pilot);
  }

  render(alpha: number, dt: number, time: number): void {
    this.volume.update(this.player.renderX, this.player.renderZ);
    /* The sky, once, then fanned out. Every material in the scene reads the same handful of
       lighting uniforms, so there is one source of truth for what time it is and no shader
       gets to disagree with another about it. */
    const env = this.sky.advance(dt);
    this.seafloor.setEnv(env);
    this.seafloor.setCaustic(env.caustic);
    this.surface.setEnv(env);
    this.volume.setEnv(env);
    this.algae.setEnv(env);
    this.shafts.setEnv(env);
    this.snow.setEnv(env);
    this.material.setEnv(env);
    this.reef.setEnv(env);
    this.fish.setEnv(env);
    this.turtles.setEnv(env);
    this.mantas.setEnv(env);
    this.feeder.setEnv(env);
    this.anglers.setEnv(env);
    this.octopus.setEnv(env);
    this.remotes.setEnv(env);
    this.shadows.setSunDir(env.sunDir);

    this.seafloor.update(time, this.player.renderX);

    /* The grass has to know what is moving through it. Everything with a body gets tracked;
       the Wake keeps a short trail per creature so the bed recovers behind them rather than
       snapping shut, and hands back only the nearest few — a wake forty units away cannot be
       seen bending anything. Radii are body-scale, so a manta parts a much wider swathe. */
    this.wake.track(0, this.player.renderX, this.player.renderY, this.player.renderZ, 3.6, time);
    this.turtles.trackInto(this.wake, 100, time);
    this.mantas.trackInto(this.wake, 200, time);
    this.octopus.trackInto(this.wake, 300, time);
    this.algae.setDisturbers(
      this.wake.build(time, MAX_DISTURBERS, this.player.renderX, this.player.renderZ),
    );
    this.algae.update(time, this.player.renderX, this.player.renderZ);
    this.material.setTime(time);
    this.player.render(alpha, dt, time);

    // The shaft field and the snow box both ride with the player so the water column is
    // continuous no matter how far the shark swims from the origin.
    this.reef.update(time, this.player.renderX);
    this.fish.update(dt, time, this.player.renderX, this.player.renderZ);
    this.octopus.update(dt, this.player.renderX, this.player.renderZ);
    this.turtles.update(dt, time, this.player.renderX);
    this.mantas.update(dt, time, this.player.renderX);
    this.anglers.update(dt, time, this.player.renderX);

    /* The lure. ONE light, the nearest, pushed to the handful of materials that declare the
       uniforms — the sand and every shark in the water. Everything else in the scene is lit by
       the sky alone, which is what keeps a point light affordable in a game that has never had
       one. */
    this.anglers.nearestLure(this.player.renderX, this.player.renderZ, this.lure);
    this.seafloor.setLure(this.lure);
    applyLure(this.material.uniforms, this.lure);
    this.remotes.applyLure(this.lure);

    /* The feeder. Solo spawns its own; in a bay the relay says when, because two sharks
       reaching the same fish is the one question here with a wrong answer. */
    if (this.bay === '' || !this.net.enabled) {
      this.soloNext -= dt;
      if (this.soloNext <= 0 && !this.feeder.live) {
        this.soloId++;
        this.feeder.show(feederAt(this.seed, this.soloId));
        this.soloNext = 1e9;
      }
    }

    const live = this.feeder.live;
    if (live && this.feeder.withinReach(
      this.player.renderX, this.player.renderY, this.player.renderZ, this.player.biting,
    )) {
      if (this.bay !== '' && this.net.connected) {
        // A request, not an assertion: the relay decides and tells everyone, including us.
        this.net.claim(live.id);
      } else {
        this.feeder.taken(1);
        this.lastWinner = 'YOU';
        this.winnerAt = performance.now() / 1000;
        this.soloNext = FEEDER_PERIOD[0] + Math.random() * (FEEDER_PERIOD[1] - FEEDER_PERIOD[0]);
      }
    }

    /* Where the prize rides. 1 is us; anything else is a peer, and if that peer is not in view
       the fish simply is not drawn — it belongs to a shark, not to the water. */
    const holder = this.feeder.heldBy;
    const pose = holder === 1
      ? { x: this.player.renderX, y: this.player.renderY, z: this.player.renderZ,
          heading: this.player.heading }
      : holder > 1 ? this.remotes.poseOf(holder) : null;
    this.feeder.update(dt, time, pose);

    /* Multiplayer. Publishing uses the RENDER pose rather than the simulation pose so peers
       receive the same interpolated position that is on screen here, and the send is rate
       limited inside NetClient. Both sides of this are no-ops when no relay is configured. */
    /* One clock, and in a reel it is the REEL's. Companions are stamped with reel seconds by
       driveCompanions, and interpolating them against performance.now() instead would leave the
       render time permanently past the newest sample — every companion dead-reckoned forever,
       drifting ahead of where it was actually put. */
    const wall = this.reelClock >= 0 ? this.reelClock : performance.now() / 1000;
    this.net.sendPose(wall, this.player.renderX, this.player.renderY, this.player.renderZ,
      this.player.heading, this.player.speed);
    this.remotes.update(wall, dt, time);
    if (this.remotes.dirty) {
      this.remotes.dirty = false;
      this.castersDirty = true;
    }
    // an individual coming or going changes the caster set
    if (this.octopus.visibleCount !== this.octopusOut) {
      this.octopusOut = this.octopus.visibleCount;
      this.castersDirty = true;
    }
    this.shafts.update(time, this.player.renderX, this.player.renderZ);
    this.snow.update(time, this.player.renderX, this.player.renderZ, this.ctx.pixelRatio());
    this.surface.update(
      time,
      this.player.renderX,
      this.player.renderZ,
      this.player.heading,
      this.player.breach,
    );

    // Shadow pass runs after every caster has been posed for this frame and before the main
    // render, so the depth twins see the same transforms the beauty pass will.
    if (this.castersDirty) this.collectCasters();
    this.shadows.render(this.ctx.gl, this.ctx.scene, this.casters, this.player.renderX, this.player.renderZ);
    // Every receiver, not just the sand. The shark's shadow crossing a coral head is what
    // puts the two of them in the same room; without it the reef is a painted backdrop.
    const depth = this.shadows.target.depthTexture;
    this.seafloor.setShadow(depth, this.shadows.matrix, SHADOW_STRENGTH);
    this.algae.setShadow(depth, this.shadows.matrix, SHADOW_STRENGTH);
    this.material.setShadow(depth, this.shadows.matrix, SHADOW_STRENGTH);
    this.reef.setShadow(depth, this.shadows.matrix, SHADOW_STRENGTH);
    this.fish.setShadow(depth, this.shadows.matrix, SHADOW_STRENGTH);
    this.octopus.setShadow(depth, this.shadows.matrix, SHADOW_STRENGTH);
    this.turtles.setShadow(depth, this.shadows.matrix, SHADOW_STRENGTH);
    this.mantas.setShadow(depth, this.shadows.matrix, SHADOW_STRENGTH);
    this.remotes.setShadow(depth, this.shadows.matrix, SHADOW_STRENGTH);

    /* Feed the soundscape. All four are things the player can already see, which is the
       point: the audio has to agree with the picture or it reads as a loop playing over the
       top of a game rather than as the place the game is in. */
    const water = waterDepth(this.player.renderX, this.player.renderZ);
    const under = OCEAN.surfaceHeight - this.player.renderY;
    this.ctx.audio.update(
      water,
      Math.min(1, Math.max(0, under / 16)),
      1 - Math.min(1, Math.max(0, (water - COAST.minSwimDepth) / 9)),
      Math.min(1, this.player.speed / MOVEMENT.cruiseSpeed),
    );

    this.followTarget.x = this.player.renderX;
    this.followTarget.y = this.player.renderY;
    this.followTarget.z = this.player.renderZ;
    this.followVel.x = this.player.vx;
    this.followVel.z = this.player.vz;
    this.ctx.camera.follow(this.followTarget, this.followVel, this.ctx.input.pointerNdc, dt);
  }

  /**
   * Everything wearing a SharkMaterial casts: the shark, the fish, the reef. The seafloor
   * and the water surface are deliberately excluded — the floor is the receiver and would
   * self-shadow, and a shadow cast by the ceiling onto the sand is just a second, wrong sun.
   */
  private collectCasters(): void {
    this.casters = [];
    this.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      if (m === this.seafloor.mesh || m === this.surface.mesh || m === this.volume.mesh) return;
      const mat = m.material;
      if (!Array.isArray(mat) && typeof (mat as { makeShadowMaterial?: unknown }).makeShadowMaterial === 'function') {
        if (!m.visible || !m.parent?.visible) return;
        this.casters.push(m);
      }
    });
    this.castersDirty = false;
  }

  resize(_width: number, _height: number): void {
    /* the camera owns the projection; nothing mode-local depends on size */
  }

  debugRows(): Array<[string, string]> {
    const p = this.player;
    return [
      ['shark', `${this.spec.name || 'unnamed'} (${this.spec.species})`],
      ['size/age', `${this.spec.size.toFixed(2)} / ${this.spec.age.toFixed(2)}`],
      ['mesh', this.status],
      ['tris', String(this.triangles)],
      ['speed', `${p.speed.toFixed(2)} u/s`],
      ['ventilation', `${(p.ventilation * 100).toFixed(0)}% of ${MOVEMENT.cruiseSpeed}`],
      ['heading', `${((p.heading * 180) / Math.PI).toFixed(0)}°`],
      ['pos', `${p.x.toFixed(1)}, ${p.z.toFixed(1)}`],
      ['depth', `${p.y.toFixed(1)} u · vy ${p.vy.toFixed(1)}`],
      ['breach', `${(p.breach * 100).toFixed(0)}%`],
      ['gape', `${((p.gape * 180) / Math.PI).toFixed(0)}°`],
      ['reef', `${this.reef.count} props · ${this.fish.count} fish`],
      ['algae', `${this.algae.bladeCount.toLocaleString()} blades`],
      ['sky', `${this.sky.clockString()} · night ${(this.sky.env.night * 100).toFixed(0)}%`],
      ['pilot', this.autopilot.status()],
      ['octopus', `${this.octopus.debugRow()} · ${this.octopus.voxelCount.toLocaleString()} vox`],
      ['turtles', this.turtles.debugRow()],
      ['mantas', this.mantas.debugRow()],
      ['reef mates', this.net.enabled
        ? (this.net.connected ? `${this.remotes.readyCount}/${this.remotes.count} · id ${this.net.id}` : 'connecting')
        : 'solo (no relay)'],
      ['lunge', p.lungeActive ? 'ACTIVE' : p.lungeCooldown > 0 ? `${p.lungeCooldown.toFixed(2)}s` : 'READY'],
    ];
  }

  /** the last feeder result, for the HUD; '' once it has been on screen long enough */
  feederFlash(): string {
    return performance.now() / 1000 - this.winnerAt < 3.4 ? this.lastWinner : '';
  }

  dispose(): void {
    this.exit();
    this.seafloor.dispose();
    this.algae.dispose();
    this.shafts.dispose();
    this.snow.dispose();
    this.surface.dispose();
    this.volume.dispose();
    this.reef.dispose();
    this.fish.dispose();
    this.octopus.dispose();
    this.turtles.dispose();
    this.mantas.dispose();
    this.remotes.dispose();
    this.net.dispose();
    this.shadows.dispose();
    this.player.dispose();
    this.material.dispose();
  }
}
